<div class="container-fluid document-inbox py-4">
  <div class="d-flex flex-wrap align-items-start justify-content-between mb-3">
    <div>
      <h2 class="mb-1"><i class="fas fa-inbox mr-2"></i>Document Inbox</h2>
      <p class="text-muted mb-0">A review workspace built from existing document activity. Current email and intake procedures remain unchanged.</p>
    </div>
    <button class="btn btn-outline-primary" type="button" (click)="load(pagination.page)" [disabled]="loading">
      <i class="fas fa-sync-alt mr-1" [class.fa-spin]="loading"></i>Refresh
    </button>
  </div>

  <div class="card shadow-sm mb-3">
    <div class="card-body py-3">
      <div class="row align-items-end">
        <div class="col-lg-5 mb-2 mb-lg-0">
          <label for="document-inbox-query" class="font-weight-bold">Search</label>
          <div class="input-group">
            <input id="document-inbox-query" type="search" class="form-control" [(ngModel)]="filters.query"
                   (keyup.enter)="applyFilters()" placeholder="Matter number, matter, filename, type, description, or source">
            <div class="input-group-append">
              <button class="btn btn-primary" type="button" (click)="applyFilters()">Search</button>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-2 mb-2 mb-lg-0">
          <label for="document-inbox-days" class="font-weight-bold">Arrival period</label>
          <select id="document-inbox-days" class="form-control" [(ngModel)]="filters.days" (change)="applyFilters()">
            <option [ngValue]="1">Last 24 hours</option>
            <option [ngValue]="7">Last 7 days</option>
            <option [ngValue]="30">Last 30 days</option>
            <option [ngValue]="90">Last 90 days</option>
          </select>
        </div>
        <div class="col-lg-5">
          <label class="font-weight-bold d-block">Review status</label>
          <div class="btn-group flex-wrap" role="group" aria-label="Review status">
            <button type="button" class="btn" [class.btn-primary]="filters.review_status === 'unreviewed'"
                    [class.btn-outline-primary]="filters.review_status !== 'unreviewed'" (click)="setReviewStatus('unreviewed')">
              Unreviewed <span class="badge badge-light ml-1">{{ counts.unreviewed }}</span>
            </button>
            <button type="button" class="btn" [class.btn-primary]="filters.review_status === 'reviewed'"
                    [class.btn-outline-primary]="filters.review_status !== 'reviewed'" (click)="setReviewStatus('reviewed')">
              Reviewed <span class="badge badge-light ml-1">{{ counts.reviewed }}</span>
            </button>
            <button type="button" class="btn" [class.btn-primary]="filters.review_status === 'all'"
                    [class.btn-outline-primary]="filters.review_status !== 'all'" (click)="setReviewStatus('all')">
              All <span class="badge badge-light ml-1">{{ counts.all }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="!loading && matters.length === 0" class="card shadow-sm empty-state text-center">
    <div class="card-body py-5">
      <i class="far fa-check-circle fa-3x text-success mb-3"></i>
      <h4>No matching documents</h4>
      <p class="text-muted mb-0">Try another review status, arrival period, or search.</p>
    </div>
  </div>

  <div *ngFor="let matter of matters" class="card shadow-sm matter-group mb-3">
    <div class="card-header matter-header" role="button" tabindex="0"
         (click)="toggleMatter(matter.matter_id)" (keyup.enter)="toggleMatter(matter.matter_id)">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center min-width-0">
          <i class="fas mr-3 text-primary" [class.fa-chevron-right]="!isExpanded(matter.matter_id)"
             [class.fa-chevron-down]="isExpanded(matter.matter_id)"></i>
          <div class="min-width-0">
            <div class="font-weight-bold text-truncate">
              <a [href]="matter.matter_url" (click)="$event.stopPropagation(); activateIntranet(matter.intranet_id)">{{ matter.matter_number }}</a>
              — {{ matter.matter_name }}
              <span class="badge badge-secondary ml-2">{{ matter.intranet_name }}</span>
            </div>
            <small class="text-muted">Latest arrival {{ matter.latest_at | date:'MMM d, y, h:mm a' }}</small>
          </div>
        </div>
        <div class="d-flex flex-wrap align-items-center justify-content-end ml-3 matter-actions">
          <span class="badge badge-primary badge-pill">{{ matter.document_count }} document{{ matter.document_count === 1 ? '' : 's' }}</span>
          <span *ngIf="matter.unreviewed_count" class="badge badge-warning badge-pill ml-1">{{ matter.unreviewed_count }} new</span>
          <span *ngIf="matter.hidden_count" class="badge badge-secondary badge-pill ml-1">{{ matter.hidden_count }} hidden</span>
          <button *ngIf="matter.hidden_count" type="button" class="btn btn-sm btn-outline-secondary ml-2"
                  [disabled]="isMatterUpdating(matter.matter_id)"
                  (click)="$event.stopPropagation(); unhideAll(matter)">
            <i class="fas fa-eye mr-1"></i>Unhide All
          </button>
          <button *ngIf="matter.unreviewed_document_ids?.length" type="button" class="btn btn-sm btn-outline-success ml-2"
                  [disabled]="isMatterUpdating(matter.matter_id)"
                  (click)="$event.stopPropagation(); reviewAll(matter)">
            <i class="fas fa-check-double mr-1"></i>Review All
          </button>
          <button *ngIf="matter.hideable_count" type="button" class="btn btn-sm btn-outline-primary ml-2"
                  [disabled]="isMatterUpdating(matter.matter_id)"
                  (click)="$event.stopPropagation(); hideAll(matter)">
            <i class="fas fa-eye-slash mr-1"></i>Hide All
          </button>
        </div>
      </div>
    </div>

    <div *ngIf="isExpanded(matter.matter_id)" class="card-body p-0">
      <div *ngFor="let document of matter.documents" class="document-row p-3" [class.reviewed]="document.reviewed_at">
        <div class="row align-items-center">
          <div class="col-auto thumbnail-column">
            <a *ngIf="document.document_url" [href]="authenticatedUrl(document.document_url)" target="_blank" rel="noopener"
               (click)="activateIntranet(document.intranet_id)">
              <img *ngIf="document.thumbnail_url" [src]="authenticatedUrl(document.thumbnail_url)"
                   class="document-thumbnail" [alt]="'Preview of ' + document.filename">
              <span *ngIf="!document.thumbnail_url" class="document-icon"><i class="far fa-file-alt"></i></span>
            </a>
            <a *ngIf="!document.document_url && document.scan_url" [href]="document.scan_url"
               (click)="activateIntranet(document.intranet_id)">
              <img *ngIf="document.thumbnail_url" [src]="authenticatedUrl(document.thumbnail_url)"
                   class="document-thumbnail" [alt]="'Preview of ' + document.filename">
              <span *ngIf="!document.thumbnail_url" class="document-icon"><i class="far fa-file-alt"></i></span>
            </a>
            <span *ngIf="!document.document_url && !document.scan_url" class="document-icon">
              <i class="far fa-file-alt"></i>
            </span>
          </div>
          <div class="col min-width-0">
            <div class="d-flex flex-wrap align-items-center mb-1">
              <a *ngIf="document.document_url" class="font-weight-bold document-name mr-2" [href]="authenticatedUrl(document.document_url)"
                 target="_blank" rel="noopener" (click)="activateIntranet(document.intranet_id)">{{ document.filename }}</a>
              <a *ngIf="!document.document_url && document.scan_url" class="font-weight-bold document-name mr-2" [href]="document.scan_url"
                 (click)="activateIntranet(document.intranet_id)">{{ document.filename }}</a>
              <span *ngIf="!document.document_url && !document.scan_url" class="font-weight-bold document-name mr-2">{{ document.filename }}</span>
              <span class="badge badge-info mr-1">{{ document.source }}</span>
              <span *ngIf="document.document_type" class="badge badge-light border mr-1">{{ document.document_type }}</span>
              <span class="badge mr-1"
                    [class.badge-success]="document.release_status === 'released'"
                    [class.badge-warning]="document.release_status !== 'released'">
                {{ document.release_status_label || (document.document_id ? 'Released' : (document.scan_id ? 'Still in scan queue' : 'Waiting for scan queue')) }}
              </span>
              <span *ngIf="document.reviewed_at" class="badge badge-success">Reviewed</span>
              <span *ngIf="!document.reviewed_at && document.reviewable !== false" class="badge badge-warning">Ready for review</span>
            </div>
            <div class="small font-weight-bold document-identifiers mb-1">
              <span *ngIf="document.document_id" class="mr-2">Document #{{ document.document_id }}</span>
              <span *ngIf="document.scan_id" class="mr-2">Scan #{{ document.scan_id }}</span>
              <span *ngIf="document.intake_id">Intake #{{ document.intake_id }}</span>
            </div>
            <p *ngIf="document.description" class="mb-1 document-description">{{ document.description }}</p>
            <div class="small text-muted">
              {{ document.history_title }} · {{ document.received_at | date:'MMM d, y, h:mm a' }}
              <span *ngIf="document.received_by"> · {{ document.received_by }}</span>
              <span *ngIf="document.intake_status"> · Intake: {{ document.intake_status_label || document.intake_status }}</span>
            </div>
            <div class="mt-2">
              <a *ngIf="document.document_url" class="mr-3" [href]="authenticatedUrl(document.document_url)" target="_blank" rel="noopener"
                 (click)="activateIntranet(document.intranet_id)">
                <i class="fas fa-external-link-alt mr-1"></i>Open document #{{ document.document_id }} — {{ document.filename }}
              </a>
              <a *ngIf="!document.document_url && document.scan_url" class="mr-3" [href]="document.scan_url"
                 (click)="activateIntranet(document.intranet_id)">
                <i class="fas fa-inbox mr-1"></i>Open scan #{{ document.scan_id }} — {{ document.filename }}
              </a>
              <a *ngIf="document.history_url" [href]="document.history_url" (click)="activateIntranet(document.intranet_id)"><i class="fas fa-history mr-1"></i>Open history</a>
              <a *ngIf="!document.history_url" [href]="matter.matter_url" (click)="activateIntranet(matter.intranet_id)"><i class="fas fa-folder-open mr-1"></i>Open matter</a>
            </div>
          </div>
          <div class="col-md-auto mt-3 mt-md-0 text-md-right">
            <button *ngIf="document.reviewable !== false && !document.reviewed_at" type="button" class="btn btn-success"
                    [disabled]="isUpdating(document.document_id)" (click)="markReviewed(document, true)">
              <i class="fas fa-check mr-1"></i>Mark reviewed
            </button>
            <button *ngIf="document.reviewed_at" type="button" class="btn btn-outline-success"
                    title="Click to return this document to unreviewed"
                    [disabled]="isUpdating(document.document_id)" (click)="markReviewed(document, false)">
              <i class="fas fa-check mr-1"></i>Reviewed
            </button>
            <button *ngIf="document.reviewable !== false" type="button" class="btn btn-outline-secondary ml-2"
                    [disabled]="isUpdating(document.document_id)" (click)="reviewAndHide(document)">
              <i class="fas fa-eye-slash mr-1"></i>Review &amp; Hide
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <pagination *ngIf="pagination.total_matters > pagination.per_page" class="pagination-sm"
              [boundaryLinks]="true" [totalItems]="pagination.total_matters"
              [itemsPerPage]="pagination.per_page" [(ngModel)]="pagination.page"
              (pageChanged)="pageChanged($event)" previousText="&lsaquo;" nextText="&rsaquo;"
              firstText="&laquo;" lastText="&raquo;"></pagination>
</div>
